import { isFunction } from 'common/utils/validator';

const baseModel = {
  name: '',
  value: '',
  // isCommercial: true,
  commercial: true,
  linkType: ''
};

export const MEDIA_TYPES = {
  WIRELINE: 'WIRELINE',
  OFFNET: 'Offnet',
  WIRELESS: 'WIRELESS',
  THIRD_PARTY: 'Third Party',
  RF_P2P: 'RF_P2P',
  RF_P2MP: 'RF_P2MP',
  FOUR_G: 'FOUR_G',
  FIVE_G: 'FIVE_G'
};

export const prepareSpecModel = (primary, secondary, isSecondaryVisible = false) => {
  const primaryKeys = Object.keys(primary);
  const secondaryKeys = Object.keys(secondary);
  const primaryReq = getModelFor(primaryKeys, primary, 'PRIMARY');
  const secondaryReq = getModelFor(secondaryKeys, secondary, 'SECONDARY');
  return isSecondaryVisible ? primaryReq.concat(secondaryReq) : primaryReq;
};

const getFormKey = (form, keyName, type) => {
  const key = form[keyName].formKey || keyName;

  if (key === 'lanIP') {
    return type === 'PRIMARY' ? 'lanIp' : 'lanIp_secondary';
  }

  if (key === 'wanIP') {
    return type === 'PRIMARY' ? 'wanIp' : 'wanIp_secondary';
  }

  return key;
};

const getModelFor = (keys, form, type) => {
  return keys.map((keyName) => {
    const model = { ...baseModel };
    model.name = getFormKey(form, keyName, type);
    // model.isCommercial = true;
    model.commercial = form[keyName].commercial;
    model.value = form[keyName].value && typeof form[keyName].value === 'object' ? form[keyName].value.id : form[keyName].value;
    model.linkType = type;
    return model;
  });
};

export const getOptions = (list, metaData, formatter) => {
  if (Array.isArray(list)) {
    return list.map((item) => {
      return {
        ...metaData,
        key: item,
        displayName: isFunction(formatter) ? formatter(item) : item,
        id: item
      };
    });
  }
  return [];
};

const getMedia = (siteSpec) => {
  if (siteSpec.length === 0) {
    return '';
  }

  const filteredSpec = siteSpec.find((item) => item.name === 'media');

  return (filteredSpec && filteredSpec.value) || '';
};

// export const getSelectedMedia = (leadDetails = {}) => {
export const getSelectedMedia = (siteSpecs) => {
  return getMedia(siteSpecs);
};

export const splittedBwValue = (bandwidthWithUnit) => {
  const [bwValue, bwUnit] = bandwidthWithUnit && bandwidthWithUnit.split(' ');

  return [bwValue, bwUnit];
};

const isValidMedia = (type, feasibilityResponse, requestedBandwidth) => {
  const { feasibilityType, wirelineNetworkElement, offNetResponse, wirelessNetworkElement, thirdPartyResponse } = feasibilityResponse;

  // maxBandwidth keys for different media types - WIRELINE (maxspeed - 1000), OFFNET (offNetMaxSupportedBandwidth - 20 Mbps), WIRELESS (maxspeed - 4 Mbps), THIRD PARTY (maxSupportedBandwidth - 20 Mbps)

  let isValid = false;

  if (!feasibilityType || !Array.isArray(feasibilityType) || !feasibilityType.length || !feasibilityType.includes(type)) {
    return isValid;
  }

  switch (type) {
    case MEDIA_TYPES.WIRELINE: {
      const maxFeasibleBw = wirelineNetworkElement && wirelineNetworkElement.maxspeed;
      const isUnitComingFromApi = maxFeasibleBw && maxFeasibleBw.includes('Mbps');
      const [bwValue] = splittedBwValue(maxFeasibleBw);
      if (wirelineNetworkElement && Object.keys(wirelineNetworkElement).length > 0 && parseInt(requestedBandwidth) <= parseInt(isUnitComingFromApi ? bwValue : maxFeasibleBw)) {
        isValid = true;
      }

      break;
    }
    case MEDIA_TYPES.OFFNET: {
      const maxFeasibleBw = offNetResponse && offNetResponse.offNetMaxSupportedBandwidth;
      const isUnitComingFromApi = maxFeasibleBw && maxFeasibleBw.includes('Mbps');
      const [bwValue] = splittedBwValue(maxFeasibleBw);
      if (offNetResponse && Object.keys(offNetResponse).length > 0 && parseInt(requestedBandwidth) <= parseInt(isUnitComingFromApi ? bwValue : maxFeasibleBw)) {
        isValid = true;
      }

      break;
    }
    case MEDIA_TYPES.WIRELESS: {
      const maxFeasibleBw = wirelessNetworkElement && wirelessNetworkElement.maxspeed;
      const isUnitComingFromApi = maxFeasibleBw && maxFeasibleBw.includes('Mbps');
      const [bwValue] = splittedBwValue(maxFeasibleBw);
      if (wirelessNetworkElement && Object.keys(wirelessNetworkElement).length > 0 && parseInt(requestedBandwidth) <= parseInt(isUnitComingFromApi ? bwValue : maxFeasibleBw)) {
        isValid = true;
      }

      break;
    }
    case MEDIA_TYPES.THIRD_PARTY: {
      const maxFeasibleBw = thirdPartyResponse && thirdPartyResponse.maxSupportedBandwidth;
      const isUnitComingFromApi = maxFeasibleBw && maxFeasibleBw.includes('Mbps');
      const [bwValue] = splittedBwValue(maxFeasibleBw);
      if (thirdPartyResponse && Object.keys(thirdPartyResponse).length > 0 && parseInt(requestedBandwidth) <= parseInt(isUnitComingFromApi ? bwValue : maxFeasibleBw)) {
        isValid = true;
      }

      break;
    }
    default: {
      isValid = false;
    }
  }

  return isValid;
};

const getRfTowerHeight = (media) => {
  if (media) {
    return media?.nearestSites?.[0].height;
  }
};

export const setMediaOptions = (feasibilityResponse, requestedBandwidth) => {
  const primaryMediaList = [];

  if (!feasibilityResponse) {
    return { primaryMediaList };
  }

  const { feasibilityStatus, wirelineNetworkElement, offNetResponse, wirelessNetworkElement, thirdPartyResponse } = feasibilityResponse;
  const isSiteManualFeasible = feasibilityStatus === 'MANUAL_INTERVENTION_REQUIRED';
  const isSitePendingThirdParty = feasibilityStatus === 'PENDING_FOR_THIRD_PARTY';

  if (isSiteManualFeasible || isSitePendingThirdParty) {
    return { primaryMediaList };
  }

  // push priority wise in the array - ['WIRELINE', 'Offnet', 'WIRELESS', 'Third Party']

  if (isValidMedia(MEDIA_TYPES.WIRELINE, feasibilityResponse, requestedBandwidth)) {
    wirelineNetworkElement['id'] = wirelineNetworkElement.media;
    wirelineNetworkElement['key'] = wirelineNetworkElement.media;
    wirelineNetworkElement['displayName'] = wirelineNetworkElement.media;

    primaryMediaList.push(wirelineNetworkElement);
  }

  if (isValidMedia(MEDIA_TYPES.OFFNET, feasibilityResponse, requestedBandwidth)) {
    offNetResponse['id'] = MEDIA_TYPES.OFFNET;
    offNetResponse['key'] = MEDIA_TYPES.OFFNET;
    offNetResponse['displayName'] = MEDIA_TYPES.OFFNET;

    primaryMediaList.push(offNetResponse);
  }

  if (isValidMedia(MEDIA_TYPES.WIRELESS, feasibilityResponse, requestedBandwidth)) {
    wirelessNetworkElement['id'] = wirelessNetworkElement.technology;
    wirelessNetworkElement['key'] = wirelessNetworkElement.technology;
    wirelessNetworkElement['displayName'] = wirelessNetworkElement.technology;
    wirelessNetworkElement['towerHeight'] = getRfTowerHeight(wirelessNetworkElement);

    primaryMediaList.push(wirelessNetworkElement);
  }

  if (isValidMedia(MEDIA_TYPES.THIRD_PARTY, feasibilityResponse, requestedBandwidth)) {
    thirdPartyResponse['id'] = MEDIA_TYPES.THIRD_PARTY;
    thirdPartyResponse['key'] = MEDIA_TYPES.THIRD_PARTY;
    thirdPartyResponse['displayName'] = MEDIA_TYPES.THIRD_PARTY;

    primaryMediaList.push(thirdPartyResponse);
  }

  return { primaryMediaList };
};

export const isRFMedia = (value) => {
  return value === MEDIA_TYPES.RF_P2P || value === MEDIA_TYPES.RF_P2MP;
};
